import { FC, useMemo } from "react";
import { View } from "react-native";

import {
  CenteredHorizontalBarChart,
  useCopyableChart,
} from "@/components/charts";
import { Card, Text, VStack } from "@/components/ui";
import { useLocalization, useThemeColors } from "@/hooks";
import SampleEditorialSummary from "./_SampleEditorialSummary";

/* ── Types ─────────────────────────────────────────────── */

export interface MaxDiffReportData {
  /** All items in the section, in their canonical order. */
  items: { id: string; name: string }[];
  /** Preference shares per item. Should sum to ~100. The chart
      centers around `100 / items.length`, so items above the
      average pull right (preferred) and below pull left. */
  shares: number[];
  /** Unique respondents underpinning the shares — drives the
      headline `(N respondents)` chip on the chart card. Counted at
      cook time from the answer set so the report stays a pure
      visualization. */
  sampleSize: number;
}

interface MaxDiffReportProps {
  data: MaxDiffReportData;
  showSampleEditorialSummary?: boolean;
}

/* ── Component ──────────────────────────────────────────── */

const MaxDiffReport: FC<MaxDiffReportProps> = ({
  data,
  showSampleEditorialSummary = false,
}) => {
  const { i18n } = useLocalization();
  const themeColors = useThemeColors();

  const { chartRef, copyButton } = useCopyableChart();

  const fmtPct = (n: number) =>
    i18n.numberToPercentage(n, {
      precision: 1,
      stripInsignificantZeros: false,
    });
  const fmtSignedPct = (n: number) =>
    `${n >= 0 ? "+" : ""}${i18n.numberToPercentage(n, {
      precision: 1,
      stripInsignificantZeros: false,
    })}`;

  const average = data.items.length > 0 ? 100 / data.items.length : 0;

  const points = useMemo(
    () =>
      data.items
        .map((it, idx) => ({ name: it.name, share: data.shares[idx] ?? 0 }))
        .sort((a, b) => b.share - a.share)
        .map((it) => ({
          label: it.name,
          tooltipLine: `${fmtPct(it.share)} · ${fmtSignedPct(it.share - average)} vs avg`,
          value: it.share - average,
        })),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [data, i18n]
  );

  if (data.items.length === 0) {
    return null;
  }

  /* No answers yet: hand the chart an empty array so it shows its own
     "No data available" state instead of a flat centered bar — mirroring
     how `PricingResult` shows "—" rather than a fabricated price. */
  const hasData = data.sampleSize > 0;

  return (
    <VStack className="gap-3">
      {showSampleEditorialSummary && (
        <SampleEditorialSummary
          body={i18n.t("useCasePage.maxDiff.sampleReport.summary.body")}
          headline={i18n.t("useCasePage.maxDiff.sampleReport.summary.headline")}
        />
      )}

      <Card className="group p-6 rounded-2xl border border-outline-200">
        {copyButton}
        <VStack className="gap-5">
          <VStack className="gap-1 pb-2">
            <Text className="text-sm font-semibold text-typography-900 dark:text-typography-100">
              {`${i18n.t("useCasePage.maxDiff.sampleReport.chart.title")} (${i18n.t("dashboardNavigator.respondents", { count: data.sampleSize })})`}
            </Text>
            <Text className="text-xs text-typography-400">
              {i18n.t("useCasePage.maxDiff.sampleReport.chart.subtitle")}
            </Text>
          </VStack>

          <View ref={chartRef} style={{ width: "100%" }}>
            <CenteredHorizontalBarChart
              formatValue={fmtSignedPct}
              negativeColor="hsl(220, 12%, 70%)"
              points={hasData ? points : []}
              positiveColor={themeColors.primary}
            />
          </View>
        </VStack>
      </Card>
    </VStack>
  );
};

export default MaxDiffReport;
